import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ChangeElementTypologyDialog,
  FormSubmitButtons,
  GridWrap,
  InfoWrapper,
  RHFNumberTextField,
  RHFSelect,
  RHFTagAutocomplete,
  RHFTextField,
} from 'components';
import { tipoElementoAggiuntivoCollegamentiOptions } from 'constants/selectOptions';
import { INFO } from 'pages/Inspections/config/info';
import {
  getSchedaDifettiElementoAggiuntivoCollegamenti as getSchedaDifetti,
  getTipologiaOptionsElementoAggiuntivoCollegamenti as getTipologiaOptions,
} from 'utils/Inspections/L1/collegamentiElementiAggiuntivi';
import { resetObjectSchedaIspezione } from 'utils/Inspections/L1/resetSchedaIspezione';
import useSchedaDifetti from 'utils/Inspections/L1/schedeDifetti';
import { isNullOrUndefinedOrEmptyString } from 'utils/utilities';

const ElementiAggiuntiviForm = ({
  elementiExtraFields,
  selected,
  setSelected,
  elementiExtraTable,
  selectedIdx,
}) => {
  const { getValues, setValue, watch } = useFormContext({
    defaultValues: {
      l0t2a6_elementiAggiuntiviTableValue: [],
      l0t2a6_nomeElementoAggiuntivoTableValue: '',
      l0t2a6_tipologiaElementoAggiuntivoTableValue: '',
      l0t2a6_tipoElementoAggiuntivoTableValue: '',
      l0t2a6_altezzaElementoAggiuntivoTableValue: '',
      l0t2a6_larghezzaElementoAggiuntivoTableValue: '',
      l0t2a6_profonditaElementoAggiuntivoTableValue: '',
      l0t2a6_superficieElementoAggiuntivoTableValue: '',
    },
  });

  const { populateSchedaDifetti } = useSchedaDifetti();

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  const tipoElementoWatch = watch('l0t2a6_tipoElementoAggiuntivoTableValue');

  useEffect(() => {
    setValue('l0t2a6_elementiAggiuntiviTableValue', selected);
  }, [selected]);

  useEffect(() => {
    onResetElementiExtra();
  }, [selectedIdx]);

  const changeAutocomplete = value => {
    setSelected(value);
  };

  const changeElementiExtra = () => {
    const nome = getValues('l0t2a6_nomeElementoAggiuntivoTableValue');
    const tipo = getValues('l0t2a6_tipoElementoAggiuntivoTableValue');
    const tipologia = getValues('l0t2a6_tipologiaElementoAggiuntivoTableValue');
    const altezza = getValues('l0t2a6_altezzaElementoAggiuntivoTableValue');
    const larghezza = getValues('l0t2a6_larghezzaElementoAggiuntivoTableValue');
    const profondita = getValues(
      'l0t2a6_profonditaElementoAggiuntivoTableValue'
    );
    const superficie = getValues(
      'l0t2a6_superficieElementoAggiuntivoTableValue'
    );

    const ids = selected.map(el => el.codice);

    const elementiExtra = elementiExtraFields.map(x => {
      if (!ids.includes(x.codice)) {
        return x;
      }

      return {
        ...x,
        nomeElementoExtra: nome,
        tipoElementoExtra: tipo,
        tipologiaElementoExtra: tipologia,
        altezzaElementoExtra: altezza,
        larghezzaElementoExtra: larghezza,
        profonditaElementoExtra: profondita,
        superficieElementoExtra: superficie,
        ...(tipologia !== x.tipologiaElementoExtra ||
        tipo !== x.tipoElementoExtra
          ? {
              ...resetObjectSchedaIspezione,
              schedaDifetti: getSchedaDifetti(
                populateSchedaDifetti,
                tipo,
                tipologia
              ).map(el => ({
                ...el,
                visto: false,
                statoDifetto: '',
                estensioneDifetto: '',
                intensitaDifetto: '',
                ps: false,
                note: '',
                media: [],
              })),
            }
          : {}),
      };
    });

    setValue(elementiExtraTable, elementiExtra, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    onResetElementiExtra();
  };

  const onConfirmChangeElementiExtra = () => {
    setOpenConfirmationDialog(false);
    changeElementiExtra();
  };

  const onSubmitElementiExtra = () => {
    const l0t2a6_tipologiaElementoExtraTableValue = getValues(
      'l0t2a6_tipologiaElementoAggiuntivoTableValue'
    );
    const l0t2a6_tipolementoAggiuntivoTableValue = getValues(
      'l0t2a6_tipoElementoAggiuntivoTableValue'
    );

    const ids = selected.map(el => el.codice);

    const elementiExtra = [...elementiExtraFields];

    const hasChangedTipologia = elementiExtra.some(el => {
      if (ids.includes(el?.codice)) {
        return (
          (l0t2a6_tipologiaElementoExtraTableValue !==
            el.tipologiaElementoExtra ||
            l0t2a6_tipolementoAggiuntivoTableValue !== el.tipoElementoExtra) &&
          !isNullOrUndefinedOrEmptyString(el.tipologiaElementoExtra)
        );
      }
    });

    if (hasChangedTipologia) {
      setOpenConfirmationDialog(true);
    } else {
      changeElementiExtra();
    }
  };

  const onResetElementiExtra = () => {
    setValue('l0t2a6_elementiAggiuntiviTableValue', []);
    setValue('l0t2a6_nomeElementoAggiuntivoTableValue', '');
    setValue('l0t2a6_tipoElementoAggiuntivoTableValue', '');
    setValue('l0t2a6_tipologiaElementoAggiuntivoTableValue', '');
    setValue('l0t2a6_altezzaElementoAggiuntivoTableValue', '');
    setValue('l0t2a6_larghezzaElementoAggiuntivoTableValue', '');
    setValue('l0t2a6_profonditaElementoAggiuntivoTableValue', '');
    setValue('l0t2a6_superficieElementoAggiuntivoTableValue', '');

    setSelected([]);
  };

  const altezzaElementoExtraWatch = parseFloat(
    watch('l0t2a6_altezzaElementoAggiuntivoTableValue')
  );
  const larghezzaElementoExtraWatch = parseFloat(
    watch('l0t2a6_larghezzaElementoAggiuntivoTableValue')
  );
  const profonditaElementoExtraWatch = parseFloat(
    watch('l0t2a6_profonditaElementoAggiuntivoTableValue')
  );

  // [2*(Profondità+Larghezza)*Altezza]/10000

  useEffect(() => {
    const result = (
      (2 *
        (profonditaElementoExtraWatch + larghezzaElementoExtraWatch) *
        altezzaElementoExtraWatch) /
      10000
    ).toFixed(2);
    setValue('l0t2a6_superficieElementoAggiuntivoTableValue', result);
  }, [
    altezzaElementoExtraWatch,
    larghezzaElementoExtraWatch,
    profonditaElementoExtraWatch,
  ]);

  return (
    <>
      <GridWrap container spacing={2} mt={2}>
        <InfoWrapper
          size={6}
          infoMessage={INFO.elementiExtraSelezionati.infoMessage}
        >
          <RHFTagAutocomplete
            multiple={true}
            id="tags-elementiExtra"
            name={'l0t2a6_elementiAggiuntiviTableValue'}
            changeAutocomplete={value => {
              changeAutocomplete(value);
            }}
            labelName={'option.codice'}
            getOptionLabel={option => option.codice}
            opt={elementiExtraFields?.filter(
              el => !selected?.some(s => s?.id === el?.id)
            )}
            label="Elementi Aggiuntivi selezionati"
          />
        </InfoWrapper>
        <GridWrap item xs={3}>
          <RHFSelect
            name="l0t2a6_tipoElementoAggiuntivoTableValue"
            label="Tipo Elemento*"
            defaultValue={''}
            onChange={e => {
              const nuovoTipo = e.target.value;
              setValue('l0t2a6_tipoElementoAggiuntivoTableValue', nuovoTipo);
              const tipologiaAttuale = getValues(
                'l0t2a6_tipologiaElementoAggiuntivoTableValue'
              );
              const tipologiaAncoraAmmissibile = getTipologiaOptions(
                nuovoTipo
              ).some(o => o.value === tipologiaAttuale);
              if (!tipologiaAncoraAmmissibile) {
                setValue('l0t2a6_tipologiaElementoAggiuntivoTableValue', '');
              }
            }}
            selectOptions={tipoElementoAggiuntivoCollegamentiOptions}
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFTextField
            name="l0t2a6_nomeElementoAggiuntivoTableValue"
            label={'Nome elemento'}
            defaultValue=""
            inputProps={{ maxLength: 50 }}
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFSelect
            name="l0t2a6_tipologiaElementoAggiuntivoTableValue"
            label="Tipologia Elemento*"
            defaultValue={''}
            onChange={e => {
              setValue(
                'l0t2a6_tipologiaElementoAggiuntivoTableValue',
                e.target.value
              );
            }}
            selectOptions={getTipologiaOptions(tipoElementoWatch)}
            disabled={!tipoElementoWatch}
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFNumberTextField
            name="l0t2a6_altezzaElementoAggiuntivoTableValue"
            label={'Altezza (H) [cm]'}
            defaultValue=""
            decimalScale={2}
            withValueLimit={({ value }) => value < 100000000}
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFNumberTextField
            name="l0t2a6_larghezzaElementoAggiuntivoTableValue"
            label={'Larghezza (L) [cm]'}
            defaultValue=""
            decimalScale={2}
            withValueLimit={({ value }) => value < 100000000}
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFNumberTextField
            name="l0t2a6_profonditaElementoAggiuntivoTableValue"
            label={'Profondità (P) [cm]'}
            defaultValue=""
            decimalScale={2}
            withValueLimit={({ value }) => value < 100000000}
          />
        </GridWrap>
        <InfoWrapper
          size={2}
          infoMessage={INFO.superficieElementoExtra.infoMessage}
        >
          <RHFNumberTextField
            name="l0t2a6_superficieElementoAggiuntivoTableValue"
            label={'Superficie [m²]'}
            defaultValue=""
            decimalScale={2}
            disabled
            InputLabelProps={{ shrink: true }}
            withValueLimit={({ value }) => value < 1000}
          />
        </InfoWrapper>
        <GridWrap item xs="auto" sx={{ ml: 'auto' }}>
          <FormSubmitButtons
            onSubmit={onSubmitElementiExtra}
            onReset={onResetElementiExtra}
          />
        </GridWrap>
      </GridWrap>
      <ChangeElementTypologyDialog
        open={openConfirmationDialog}
        onClose={() => setOpenConfirmationDialog(false)}
        onConfirm={onConfirmChangeElementiExtra}
      />
    </>
  );
};
ElementiAggiuntiviForm.propTypes = {
  elementiExtraFields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      nomeElementoExtra: PropTypes.string,
      tipologiaElementoExtra: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      altezzaElementoExtra: PropTypes.number,
      larghezzaElementoExtra: PropTypes.number,
      profonditaElementoExtra: PropTypes.number,
      superficieElementoExtra: PropTypes.number,
    })
  ),
  selected: PropTypes.array,
  setSelected: PropTypes.func,
  elementiExtraTable: PropTypes.string,
  selectedIdx: PropTypes.number,
};
export default ElementiAggiuntiviForm;
